'use client';

import { Button } from '@ui/components/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ui/components/card';
import { Input } from '@ui/components/input';
import { Label } from '@ui/components/label';
import { text as settingsText } from '@modules/settings/text';

import { PageContentLoading } from '@/src/components/PageContentLoading';
import type { ChangePasswordProps } from './change_password_view_props';
export type { ChangePasswordProps } from './change_password_view_props';

export function ChangePasswordForm({
  isLoading,
  canChangePassword,
  title,
  subtitle,
  submitButtonLabel,
  unavailableMessage,
  currentPasswordLabel,
  newPasswordLabel,
  confirmPasswordLabel,
  isSubmitting,
  currentPassword,
  newPassword,
  confirmPassword,
  onCurrentPasswordChange,
  onNewPasswordChange,
  onConfirmPasswordChange,
  onSubmit,
  onNavigateBack,
  onFormSubmit,
}: ChangePasswordProps) {
  if (isLoading) {
    return <PageContentLoading />;
  }

  return (
    <Card className="w-full max-w-md py-5">
      <CardHeader className="text-center">
        <CardTitle className="text-2xl">{title}</CardTitle>
        <CardDescription>{subtitle}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        {!canChangePassword ? (
          <p className="text-muted-foreground text-center text-sm">{unavailableMessage}</p>
        ) : (
          <form onSubmit={onFormSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="currentPassword">{currentPasswordLabel}</Label>
              <Input
                id="currentPassword"
                type="password"
                value={currentPassword}
                onChange={(event) => onCurrentPasswordChange(event.target.value)}
                placeholder="Enter your current password"
                autoComplete="current-password"
                disabled={isSubmitting}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="newPassword">{newPasswordLabel}</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(event) => onNewPasswordChange(event.target.value)}
                placeholder="Enter your new password"
                autoComplete="new-password"
                disabled={isSubmitting}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="confirmPassword">{confirmPasswordLabel}</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(event) => onConfirmPasswordChange(event.target.value)}
                placeholder="Confirm your new password"
                autoComplete="new-password"
                disabled={isSubmitting}
              />
            </div>

            <Button
              type="button"
              className="w-full"
              size="lg"
              disabled={isSubmitting}
              onClick={onSubmit}>
              {submitButtonLabel}
            </Button>
          </form>
        )}

        <Button
          type="button"
          variant="outline"
          className="w-full"
          size="lg"
          onClick={onNavigateBack}>
          {settingsText.backToSettings}
        </Button>
      </CardContent>
    </Card>
  );
}
